import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Dialog } from '@/components/ui/Dialog';
import { informer } from '@/components/ui/Fenetres';
import { useBonsCarburant, useCamions, useCreateBonCarburant, useMines } from '@/hooks/useTransport';
import { useAuthStore } from '@/stores/authStore';
import { extractError } from '@/api/client';
import { useOnlineStatus } from '@/offline/useOnlineStatus';
import { offlineDB } from '@/offline/offlineDB';
import { RATIO_GRAVITE_COLORS, RATIO_GRAVITE_ICONS, RATIO_GRAVITE_LABELS, type RatioGraviteCarburant } from '@/types';

const DEFAULT_CONSO = 50; // L/100km défaut brief §5.3

/**
 * Brief §5.3 — Saisie d'un bon de carburant.
 * Calcul ratio temps réel + alerte couleur si gravité ≥ élevé (115%).
 */
interface Props {
  open:    boolean;
  onClose: () => void;
}

export const SaisieBonCarburantDialog = ({ open, onClose }: Props) => {
  const user = useAuthStore((s) => s.user);
  const filialeKey = useAuthStore((s) => s.filiale?.filialeKey);
  const online = useOnlineStatus();
  const create = useCreateBonCarburant();
  const { data: minesData }   = useMines({ actif: true, limit: 100 });
  const { data: camionsData } = useCamions({ actif: true, limit: 500 });
  const mines   = minesData?.data   ?? [];
  const camions = camionsData?.data ?? [];

  // Section 1 — Identification
  const [mineId, setMineId]               = useState('');
  const [dateHeure, setDateHeure]         = useState('');
  const [agentNom, setAgentNom]           = useState('');
  // Section 2 — Véhicule
  const [camionId, setCamionId]           = useState('');
  const [chauffeurNom, setChauffeurNom]   = useState('');
  const [kmCompteur, setKmCompteur]       = useState('');
  const [photoUrl, setPhotoUrl]           = useState('');
  // Section 3 — Quantités
  const [qteDemandee, setQteDemandee]     = useState('');
  const [qteServie, setQteServie]         = useState('');
  const [raisonEcart, setRaisonEcart]     = useState('');
  // Section 5 — Signatures
  const [signedChauffeur, setSignedChauffeur] = useState(false);
  const [signedAgent, setSignedAgent]     = useState(false);

  const [error, setError]   = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setMineId(''); setCamionId(''); setChauffeurNom('');
    setDateHeure(toLocalDatetime(new Date()));
    setAgentNom(user?.fullName ?? '');
    setKmCompteur(''); setPhotoUrl('');
    setQteDemandee(''); setQteServie(''); setRaisonEcart('');
    setSignedChauffeur(false); setSignedAgent(false);
    setError(null);
  }, [open, user]);

  // Charger dernier bon du camion pour le panel info rapide + calcul km
  const { data: dernierBons } = useBonsCarburant({
    camionId: camionId || undefined,
    statut:   'valide',
    limit:    1,
  });
  const dernier = camionId && dernierBons?.data?.length ? dernierBons.data[0] : null;

  // Camion sélectionné → auto-fill chauffeur si pas saisi
  const camion = useMemo(() => camions.find((c) => c.id === camionId), [camions, camionId]);
  useEffect(() => {
    if (!camion) return;
    if (!chauffeurNom && camion.chauffeurNom) setChauffeurNom(camion.chauffeurNom);
  }, [camion, chauffeurNom]);

  // Mine sélectionnée → si camion a déjà un secteur, suggère le port — non utilisé ici (différent brief)

  // ── Calculs temps réel (brief §5.3) ──
  type CalcOk  = { ok: true; kmDepuis: number; consoTheo: number; consoReelle: number; ratioPct: number; gravite: RatioGraviteCarburant; ecart: number };
  type CalcErr = { ok: false; error: string };
  const calc: CalcOk | CalcErr | null = useMemo(() => {
    const km = parseInt(kmCompteur, 10);
    const ql = parseFloat(qteServie);
    if (!km || !ql || !camion || !dernier) return null;
    if (km <= dernier.kmCompteur) return { ok: false, error: `Km ${km} ≤ dernier bon validé (${dernier.kmCompteur})` };
    const kmDepuis  = km - dernier.kmCompteur;
    const consoTheo100 = camion.consoTheoriqueL100km ? parseFloat(camion.consoTheoriqueL100km) : DEFAULT_CONSO;
    const consoTheo = (kmDepuis * consoTheo100) / 100;
    const ratioPct  = consoTheo > 0 ? (ql / consoTheo) * 100 : 100;
    let gravite: RatioGraviteCarburant;
    if (ratioPct < 85)       gravite = 'faible';
    else if (ratioPct < 115) gravite = 'normal';
    else if (ratioPct < 130) gravite = 'eleve';
    else                     gravite = 'anomalie';
    return { ok: true, kmDepuis, consoTheo, consoReelle: ql, ratioPct, gravite, ecart: ql - consoTheo };
  }, [kmCompteur, qteServie, camion, dernier]);

  const ecartObligatoire = qteDemandee && qteServie &&
    Math.abs(parseFloat(qteDemandee) - parseFloat(qteServie)) > 0.01 &&
    !raisonEcart.trim();

  const canSubmit = mineId && camionId && chauffeurNom.trim() && dateHeure &&
    kmCompteur && qteDemandee && qteServie && signedChauffeur && signedAgent &&
    !ecartObligatoire && (!calc || calc.ok);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const payload = {
      mineId,
      camionId,
      chauffeurNom:      chauffeurNom.trim(),
      dateHeure:         new Date(dateHeure).toISOString(),
      kmCompteur:        parseInt(kmCompteur, 10),
      qteDemandeeL:      parseFloat(qteDemandee),
      qteServieL:        parseFloat(qteServie),
      raisonEcart:       raisonEcart.trim() || undefined,
      agentCarburantNom: agentNom.trim() || undefined,
      photoCompteurUrl:  photoUrl.trim() || undefined,
      signedByChauffeur: signedChauffeur,
      signedByAgent:     signedAgent,
    };
    try {
      if (online) {
        await create.mutateAsync(payload);
      } else {
        if (!filialeKey) throw new Error('Filiale non identifiée — impossible de sauvegarder hors ligne');
        const mineCode   = mines.find((m) => m.id === mineId)?.code   ?? '?';
        const camionCode = camions.find((c) => c.id === camionId)?.code ?? '?';
        await offlineDB.bonsCarburant.add({
          payload,
          preview: {
            camionCode,
            mineCode,
            chauffeur:  chauffeurNom.trim(),
            qteServie:  parseFloat(qteServie),
            dateHeure:  payload.dateHeure,
          },
          createdAt:  new Date().toISOString(),
          syncStatus: 'pending',
          filialeKey,
        });
        informer('📴 Hors ligne — bon de carburant sauvegardé localement', 'Sera synchronisé au retour de connexion.');
      }
      onClose();
    } catch (err) {
      setError(extractError(err).message);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Saisir un bon de carburant"
      subtitle={`Agent : ${user?.fullName ?? '—'} · ratio calculé en temps réel`}
      size="lg"
    >
      <form onSubmit={onSubmit} className="space-y-3">
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            <AlertTriangle className="inline size-3 mr-1" />{error}
          </div>
        )}

        <Section title="1 · Identification du bon">
          <div className="grid grid-cols-3 gap-3">
            <Field label="Dépôt (mine) *">
              <select value={mineId} onChange={(e) => setMineId(e.target.value)} required className="ds-fi" style={{ width: '100%' }}>
                <option value="">— Sélectionner —</option>
                {mines.map((m) => <option key={m.id} value={m.id}>{m.code} · {m.nom}</option>)}
              </select>
            </Field>
            <Field label="Date / heure *">
              <input type="datetime-local" value={dateHeure} onChange={(e) => setDateHeure(e.target.value)} required className="ds-fi" style={{ width: '100%' }} />
            </Field>
            <Field label="Agent carburant">
              <input value={agentNom} onChange={(e) => setAgentNom(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="Nom de l'agent" />
            </Field>
          </div>
        </Section>

        <Section title="2 · Véhicule & chauffeur">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Camion *">
              <select value={camionId} onChange={(e) => setCamionId(e.target.value)} required className="ds-fi" style={{ width: '100%' }}>
                <option value="">— Sélectionner —</option>
                {camions.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.code} · {c.modele ?? ''} {c.immatriculation ?? ''}{c.consoTheoriqueL100km ? ` · théo ${c.consoTheoriqueL100km}L/100` : ''}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Chauffeur *">
              <input value={chauffeurNom} onChange={(e) => setChauffeurNom(e.target.value)} required className="ds-fi" style={{ width: '100%' }} />
            </Field>
          </div>
          {dernier && (
            <div style={{
              padding: 8, fontSize: 11, background: 'rgba(192,0,0,.04)', border: '1px solid var(--bdr)', borderRadius: 4,
              color: 'var(--tx-2)', marginTop: 4,
            }}>
              ℹ️ Dernier plein du camion : <strong>{dernier.kmCompteur} km</strong> le {new Date(dernier.dateHeure).toLocaleDateString('fr-FR')} · {parseFloat(dernier.qteServieL).toFixed(0)}L servis
              {dernier.gravite && dernier.gravite !== 'normal' ? (
                <span style={{ marginLeft: 8, color: RATIO_GRAVITE_COLORS[dernier.gravite as RatioGraviteCarburant] }}>
                  · ratio {parseFloat(dernier.ratioPct ?? '0').toFixed(0)}% {RATIO_GRAVITE_ICONS[dernier.gravite as RatioGraviteCarburant]}
                </span>
              ) : null}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Km compteur *">
              <input type="number" inputMode="numeric" min={0} value={kmCompteur} onChange={(e) => setKmCompteur(e.target.value)} required className="ds-fi" style={{ width: '100%' }}
                placeholder={dernier ? `> ${dernier.kmCompteur}` : '—'} />
            </Field>
            <Field label="Photo compteur (URL — optionnel)">
              <input value={photoUrl} onChange={(e) => setPhotoUrl(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="https://..." />
            </Field>
          </div>
        </Section>

        <Section title="3 · Quantités">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Quantité demandée (L) *">
              <input type="number" inputMode="decimal" min={0} step="0.1" value={qteDemandee} onChange={(e) => setQteDemandee(e.target.value)} required className="ds-fi" style={{ width: '100%' }} />
            </Field>
            <Field label="Quantité servie (L) *">
              <input type="number" inputMode="decimal" min={0} step="0.1" value={qteServie} onChange={(e) => setQteServie(e.target.value)} required className="ds-fi" style={{ width: '100%' }} />
            </Field>
          </div>
          {ecartObligatoire && (
            <Field label="Raison écart * (obligatoire si demandé ≠ servi)">
              <input value={raisonEcart} onChange={(e) => setRaisonEcart(e.target.value)} required className="ds-fi" style={{ width: '100%' }} placeholder="Ex : réservoir plein avant max, refus chauffeur…" />
            </Field>
          )}
          {!ecartObligatoire && qteDemandee && qteServie && (
            <Field label="Raison écart (optionnel)">
              <input value={raisonEcart} onChange={(e) => setRaisonEcart(e.target.value)} className="ds-fi" style={{ width: '100%' }} />
            </Field>
          )}
        </Section>

        {/* Section 4 — Calculs auto (brief §5.3) */}
        {calc?.ok && (
          <div style={{
            padding: 12, borderRadius: 'var(--r-sm)',
            border: `2px solid ${RATIO_GRAVITE_COLORS[calc.gravite]}`,
            background: `${RATIO_GRAVITE_COLORS[calc.gravite]}10`,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx-2)', textTransform: 'uppercase', marginBottom: 8 }}>
              4 · Calculs automatiques
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, fontSize: 12 }}>
              <Stat label="Km depuis dernier plein" value={`${calc.kmDepuis} km`} />
              <Stat label="Conso théorique"          value={`${calc.consoTheo.toFixed(1)} L`} />
              <Stat label="Conso réelle"              value={`${calc.consoReelle.toFixed(1)} L`} />
              <Stat label="Écart"                     value={`${calc.ecart >= 0 ? '+' : ''}${calc.ecart.toFixed(1)} L`} color={calc.ecart > 0 ? '#dc2626' : '#10b981'} />
              <Stat label="Ratio réel/théorique"      value={`${calc.ratioPct.toFixed(1)}%`}  color={RATIO_GRAVITE_COLORS[calc.gravite]} strong />
              <Stat label="Gravité"                   value={`${RATIO_GRAVITE_ICONS[calc.gravite]} ${RATIO_GRAVITE_LABELS[calc.gravite]}`} color={RATIO_GRAVITE_COLORS[calc.gravite]} strong />
            </div>
            {(calc.gravite === 'eleve' || calc.gravite === 'anomalie') && (
              <div style={{ marginTop: 8, padding: 6, background: '#fff', borderRadius: 4, fontSize: 11, color: '#dc2626' }}>
                ⚠️ Une <strong>anomalie carburant</strong> sera créée automatiquement à la validation
                {calc.gravite === 'anomalie' && ' (gravité grave — notification Directeur de Site)'}.
              </div>
            )}
          </div>
        )}
        {calc && !calc.ok && (
          <div style={{ padding: 8, background: '#fee2e2', borderRadius: 4, fontSize: 12, color: '#dc2626' }}>
            🛑 {calc.error}
          </div>
        )}

        <Section title="5 · Signatures">
          <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <input type="checkbox" checked={signedChauffeur} onChange={(e) => setSignedChauffeur(e.target.checked)} required />
              Signature chauffeur ✓
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <input type="checkbox" checked={signedAgent} onChange={(e) => setSignedAgent(e.target.checked)} required />
              Signature agent carburant ✓
            </label>
          </div>
        </Section>

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={!canSubmit || create.isPending}>
            {create.isPending ? 'Validation…' : 'Valider le bon'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <fieldset style={{ border: '1px solid var(--bdr)', borderRadius: 'var(--r-sm)', padding: 10 }} className="space-y-2">
    <legend style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx-2)', textTransform: 'uppercase', letterSpacing: 0.5, padding: '0 6px' }}>{title}</legend>
    {children}
  </fieldset>
);

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div><label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>{children}</div>
);

const Stat = ({ label, value, color, strong }: { label: string; value: string; color?: string; strong?: boolean }) => (
  <div>
    <div style={{ fontSize: 10, color: 'var(--tx-3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
    <div style={{ fontSize: strong ? 16 : 13, fontWeight: strong ? 700 : 600, color: color ?? 'var(--tx-1)', fontVariantNumeric: 'tabular-nums', letterSpacing: 'normal' }}>{value}</div>
  </div>
);

function toLocalDatetime(d: Date) {
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60_000);
  return local.toISOString().slice(0, 16);
}
